import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  FunnelIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  BoltIcon,
  FireIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MinusIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { timelineApi } from '../services/timelineApi';
import { callsAPI } from '../services/callApi';
import { emailApi } from '../services/emailApi';
import { noteApi } from '../services/noteApi';
import { TimelineEvent, TimelineFilters } from '../types/Timeline';
import { Call } from '../types/Call';
import { Email } from '../types/Email';
import { Note } from '../types/Note';
import { Form } from '../types/Form';
import CallDetailModal from './CallDetailModal';
import EmailDetailModal from './EmailDetailModal';
import FormDetailModal from './FormDetailModal';

interface LeadTimelineTabProps {
  leadId: string;
  leadName: string;
  leadEmail: string;
}

// Module color tokens (outline-only)
const MODULE_COLORS: Record<string, { border: string; text: string; bg: string }> = {
  CALLS:      { border: "border-emerald-600", text: "text-emerald-700", bg: "bg-emerald-50" },
  EMAILS:     { border: "border-indigo-600",  text: "text-indigo-700",  bg: "bg-indigo-50"  },
  NOTES:      { border: "border-amber-600",   text: "text-amber-700",   bg: "bg-amber-50"   },
  TASKS:      { border: "border-sky-600",     text: "text-sky-700",     bg: "bg-sky-50"     },
  DOCUMENTS:  { border: "border-orange-600",  text: "text-orange-700",  bg: "bg-orange-50"  },
  FORMS:      { border: "border-violet-600",  text: "text-violet-700",  bg: "bg-violet-50"  },
  LEADS:      { border: "border-blue-600",    text: "text-blue-700",    bg: "bg-blue-50"    },
  REFERENCES: { border: "border-teal-600",    text: "text-teal-700",    bg: "bg-teal-50"    },
  SERVICES:   { border: "border-purple-600",  text: "text-purple-700",  bg: "bg-purple-50"  },
};

// Format day label
function formatDayLabel(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); // e.g., "Fri, Sep 12"
}

// Hook: track the active item + chip Y
type UseRailChipArgs = {
  container?: HTMLElement | null;      // scroll container (null = window)
  markerEls: HTMLElement[];            // refs to each event's rail marker
  headerOffset?: number;               // px from top (to clear sticky header)
};

function useRailChip({ container, markerEls, headerOffset = 72 }: UseRailChipArgs) {
  const [state, setState] = useState({ index: 0, y: 0 });

  // rAF scroll tracker (more responsive than IntersectionObserver for this use)
  useEffect(() => {
    if (!markerEls.length) return;

    const onScroll = () => {
      const viewportY = headerOffset + 120; // target line inside sticky wrapper
      // Find marker closest to that line (relative to viewport)
      let closest = 0;
      let minDist = Infinity;
      markerEls.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        const dist = Math.abs(rect.top - viewportY);
        if (dist < minDist) { minDist = dist; closest = i; }
      });

      // Chip Y is marker's top relative to sticky wrapper
      const wrapperTop = (document.querySelector("#rail-chip-sticky") as HTMLElement)
        ?.getBoundingClientRect().top ?? headerOffset;
      const markerTop = markerEls[closest].getBoundingClientRect().top;
      const y = markerTop - wrapperTop - 8; // 8px nudge to center on dot

      
      setState({ index: closest, y });
    };

    const handle = () => {
      onScroll();
      req = requestAnimationFrame(handle);
    };
    let req = requestAnimationFrame(handle);
    return () => cancelAnimationFrame(req);
  }, [container, markerEls, headerOffset]);

  return state; // { index, y }
}

// Sticky chip component
function StickyRailChip({
  items,
  markerRefs,
}: {
  items: TimelineEvent[];
  markerRefs: React.RefObject<HTMLSpanElement | null>[];
}) {
  const markerEls = useMemo(
    () => markerRefs.map(r => r.current).filter(Boolean) as HTMLSpanElement[],
    [markerRefs]
  );

  const { index, y } = useRailChip({ container: null, markerEls, headerOffset: 72 });
  const active = items[index] ?? items[0];
  const color = MODULE_COLORS[active?.module?.toUpperCase()] ?? MODULE_COLORS.LEADS;
  
  // Debug logging
  

  return (
    <div id="rail-chip-sticky" className="sticky top-[72px] h-0 pointer-events-none z-50">
      <div
        style={{ transform: `translateY(${y + 40}px)`, transition: "transform 160ms cubic-bezier(.2,.8,.2,1)" }}
        className="pointer-events-auto z-50 -ml-8"
      >
        {/* Pill-shaped Date Chip - Positioned below module icon, more to the left */}
        <div className={`inline-flex items-center px-3 py-1.5 rounded-full ${color.bg} ${color.text} shadow-lg border ${color.border}`}>
          <span className="text-xs font-semibold">
            {formatDayLabel(active.createdAt).replace(",", " •")}
          </span>
        </div>
      </div>
    </div>
  );
}

const LeadTimelineTab: React.FC<LeadTimelineTabProps> = ({ 
  leadId, 
  leadName, 
  leadEmail 
}) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TimelineFilters>({
    page: 1,
    limit: 50,
    module: '',
    action: '',
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useState<'comfortable' | 'condensed'>('comfortable');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [savedViews, setSavedViews] = useState<string[]>(['My updates this week', 'Only calls & emails']);
  const [selectedView, setSelectedView] = useState<string>('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const markerRefs = useRef<React.RefObject<HTMLSpanElement | null>[]>([]);
  
  // Modal states
  const [showCallModal, setShowCallModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  
  // Entity data states
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  
  // Loading states for entity fetching
  const [loadingEntity, setLoadingEntity] = useState(false);

  const fetchEvents = useCallback(async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setEvents([]); // Clear existing events for fresh load
        setCurrentPage(1); // Reset to page 1 for fresh load
      }
      setError(null);
      
      // For load more, increment the page number
      const currentFilters = isLoadMore 
        ? { ...filters, page: currentPage + 1 }
        : { ...filters, page: 1 };
      
      const response = await timelineApi.getTimelineEventsByLead(parseInt(leadId), currentFilters);
      
      if (response.success) {
        if (isLoadMore) {
          setEvents(prev => [...prev, ...response.data]);
          setCurrentPage(prev => prev + 1);
        } else {
          setEvents(response.data);
          setCurrentPage(1);
        }
        
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setHasMore(response.pagination.hasNextPage);
        }
      } else {
        setError(response.message || 'Failed to fetch timeline events');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch timeline events');
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [leadId, filters]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Initialize marker refs when events change
  useEffect(() => {
    markerRefs.current = events.map(() => ({ current: null }));
    
  }, [events]);

  // Scroll detection for back to top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleFilterChange = (key: keyof TimelineFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    setCurrentPage(1);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      fetchEvents(true);
    }
  };

  const toggleEventExpansion = (eventId: number) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getModuleColor = (module: string) => {
    switch (module) {
      case 'leads': return 'bg-blue-50 text-blue-600';
      case 'tasks': return 'bg-green-50 text-green-600';
      case 'references': return 'bg-purple-50 text-purple-600';
      case 'calls': return 'bg-emerald-50 text-emerald-600';
      case 'emails': return 'bg-indigo-50 text-indigo-600';
      case 'documents': return 'bg-orange-50 text-orange-600';
      case 'forms': return 'bg-pink-50 text-pink-600';
      case 'notes': return 'bg-yellow-50 text-yellow-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getModuleLineColor = (module: string) => {
    switch (module) {
      case 'leads': return 'bg-blue-400';
      case 'tasks': return 'bg-green-400';
      case 'references': return 'bg-purple-400';
      case 'calls': return 'bg-emerald-400';
      case 'emails': return 'bg-indigo-400';
      case 'documents': return 'bg-orange-400';
      case 'forms': return 'bg-pink-400';
      case 'notes': return 'bg-yellow-400';
      default: return 'bg-gray-400';
    }
  };

  const getModuleTextColor = (module: string) => {
    switch (module) {
      case 'leads': return 'text-blue-600';
      case 'tasks': return 'text-green-600';
      case 'references': return 'text-purple-600';
      case 'calls': return 'text-emerald-600';
      case 'emails': return 'text-indigo-600';
      case 'documents': return 'text-orange-600';
      case 'forms': return 'text-pink-600';
      case 'notes': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getModuleBorderColor = (module: string) => {
    switch (module) {
      case 'leads': return 'border-blue-600';
      case 'tasks': return 'border-green-600';
      case 'references': return 'border-purple-600';
      case 'calls': return 'border-emerald-600';
      case 'emails': return 'border-indigo-600';
      case 'documents': return 'border-orange-600';
      case 'forms': return 'border-pink-600';
      case 'notes': return 'border-yellow-600';
      default: return 'border-gray-600';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create': return 'border-green-600 text-green-700 bg-white';
      case 'update': return 'border-blue-600 text-blue-700 bg-white';
      case 'delete': return 'border-rose-600 text-rose-700 bg-white';
      case 'status_change': return 'border-violet-600 text-violet-700 bg-white';
      case 'link': return 'border-amber-600 text-amber-700 bg-white';
      case 'attach': return 'border-amber-600 text-amber-700 bg-white';
      case 'share': return 'border-amber-600 text-amber-700 bg-white';
      default: return 'border-gray-600 text-gray-700 bg-white';
    }
  };

  const getModuleIcon = (module: string) => {
    const iconClass = "w-5 h-5";
    
    switch (module) {
      case 'leads':
        return <UserIcon className={`${iconClass}`} />;
      case 'tasks':
        return <CheckCircleIcon className={`${iconClass}`} />;
      case 'references':
        return <UserIcon className={`${iconClass}`} />;
      case 'calls':
        return <PhoneIcon className={`${iconClass}`} />;
      case 'emails':
        return <EnvelopeIcon className={`${iconClass}`} />;
      case 'documents':
        return <DocumentTextIcon className={`${iconClass}`} />;
      case 'forms':
        return <DocumentTextIcon className={`${iconClass}`} />;
      case 'notes':
        return <PencilIcon className={`${iconClass}`} />;
      default:
        return <ClockIcon className={`${iconClass}`} />;
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const eventDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - eventDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    return eventDate.toLocaleDateString();
  };

  const formatDayChip = (dateString: string) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${dayName} • ${monthDay}`;
  };

  const groupEventsByDay = (events: TimelineEvent[]) => {
    const groups: { [key: string]: TimelineEvent[] } = {};
    events.forEach(event => {
      const dateKey = new Date(event.createdAt).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    return groups;
  };


  const formatFieldDiff = (field: string, change: any) => {
    if (change.from === change.to) return null;
    
    return (
      <div className="flex items-center space-x-2 text-sm">
        <span className="font-medium text-gray-600">{field}:</span>
        <span className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs line-through">
          {change.from || 'empty'}
        </span>
        <ArrowRightIcon className="w-3 h-3 text-gray-400" />
        <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs">
          {change.to || 'empty'}
        </span>
      </div>
    );
  };


  const isEventExpanded = (eventId: number) => expandedEvents.has(eventId);

  const handleDeleteEvent = async (eventId: number) => {
    try {
      const response = await timelineApi.deleteTimelineEvent(eventId);
      if (response.success) {
        setEvents(prev => prev.filter(event => event.id !== eventId));
        setShowDeleteConfirm(false);
        setEventToDelete(null);
      } else {
        setError(response.message || 'Failed to delete timeline event');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete timeline event');
    }
  };

  const handleNavigateToEntity = async (timelineEvent: TimelineEvent, domEvent: React.MouseEvent) => {
    const { module, entityId } = timelineEvent;
    
    // Prevent event bubbling
    domEvent.stopPropagation();
    
    // Navigate based on module type
    switch (module) {
      case 'tasks':
        // Navigate to task detail page
        window.open(`/tasks/${entityId}`, '_blank');
        break;
      case 'leads':
        // Navigate to lead detail page
        window.open(`/leads/${entityId}`, '_blank');
        break;
      case 'calls':
        // Fetch call data and show modal
        await fetchAndShowCall(entityId);
        break;
      case 'emails':
        // Fetch email data and show modal
        await fetchAndShowEmail(entityId);
        break;
      case 'documents':
        // Navigate to documents module and highlight the specific document
        window.open(`/documents?highlight=${entityId}`, '_blank');
        break;
      case 'forms':
        // Fetch form data and show modal
        await fetchAndShowForm(entityId);
        break;
      case 'notes':
        // Fetch note data and show modal
        await fetchAndShowNote(entityId);
        break;
      case 'references':
        // Navigate to reference detail page
        window.open(`/references/${entityId}`, '_blank');
        break;
      default:
        console.warn(`Navigation not implemented for module: ${module}`);
        break;
    }
  };

  // Fetch and show call modal
  const fetchAndShowCall = async (callId: number) => {
    try {
      setLoadingEntity(true);
      const call = await callsAPI.getCallById(callId);
      setSelectedCall(call);
      setShowCallModal(true);
    } catch (error) {
      console.error('Failed to fetch call:', error);
      alert('Failed to load call details. Please try again.');
    } finally {
      setLoadingEntity(false);
    }
  };

  // Fetch and show email modal
  const fetchAndShowEmail = async (emailId: number) => {
    try {
      setLoadingEntity(true);
      const email = await emailApi.getEmail(emailId);
      setSelectedEmail(email);
      setShowEmailModal(true);
    } catch (error) {
      console.error('Failed to fetch email:', error);
      alert('Failed to load email details. Please try again.');
    } finally {
      setLoadingEntity(false);
    }
  };

  // Fetch and show note modal
  const fetchAndShowNote = async (noteId: number) => {
    try {
      setLoadingEntity(true);
      const response = await noteApi.getNoteById(noteId);
      if (response.success) {
        setSelectedNote(response.data);
        setShowNoteModal(true);
      } else {
        alert('Failed to load note details. Please try again.');
      }
    } catch (error) {
      console.error('Failed to fetch note:', error);
      alert('Failed to load note details. Please try again.');
    } finally {
      setLoadingEntity(false);
    }
  };


  // Fetch and show form modal
  const fetchAndShowForm = async (formId: number) => {
    try {
      setLoadingEntity(true);
      // For now, show a message since we don't have a form API yet
      alert(`Form details are available in the Forms module. Form ID: ${formId}`);
    } catch (error) {
      console.error('Failed to fetch form:', error);
      alert('Failed to load form details. Please try again.');
    } finally {
      setLoadingEntity(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Sticky Header Bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
        <div>
              <h1 className="text-lg font-semibold text-gray-900">Activity Timeline</h1>
              <p className="text-sm text-gray-500">for {leadName}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-64 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Density Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setDensity('comfortable')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  density === 'comfortable' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Comfortable
              </button>
              <button
                onClick={() => setDensity('condensed')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  density === 'condensed' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Condensed
              </button>
        </div>
        
            {/* Save View Dropdown */}
            <select
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Activities</option>
              {savedViews.map(view => (
                <option key={view} value={view}>{view}</option>
              ))}
            </select>
            
            {/* Filter Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <FunnelIcon className="h-4 w-4 mr-2" />
          Filters
        </button>
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showAdvancedFilters ? 'Hide Advanced' : 'Show Advanced'}
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Module</label>
              <select
                value={filters.module || ''}
                onChange={(e) => handleFilterChange('module', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Modules</option>
                <option value="leads">Leads</option>
                <option value="tasks">Tasks</option>
                <option value="references">References</option>
                <option value="calls">Calls</option>
                <option value="emails">Emails</option>
                <option value="documents">Documents</option>
                <option value="forms">Forms</option>
                <option value="notes">Notes</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
              <select
                value={filters.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="status_change">Status Change</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">User</label>
              <select className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">All Users</option>
                <option value="me">Only Me</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                min={filters.startDate || undefined}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="hidden">
              <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
              <select className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
            
            <div className="flex items-end gap-2">
              <button
                onClick={() => {
                  setFilters({
                    page: 1,
                    limit: 50,
                    module: '',
                    action: '',
                    startDate: '',
                    endDate: ''
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Clear
              </button>
              <button 
                onClick={() => fetchEvents()}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* White-First Timeline */}
      <div className="px-6 py-8">
      {events.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto h-24 w-24 text-gray-300 mb-4">
            <ClockIcon className="h-24 w-24" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Timeline Events</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              No activities have been recorded for this lead yet.
            </p>
        </div>
        ) : (
          <div className="relative">
            {/* Left vertical rail */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-neutral-200" aria-hidden />

            {/* Sticky chip following the rail */}
            <StickyRailChip items={events} markerRefs={markerRefs.current as React.RefObject<HTMLSpanElement | null>[]} />
            
            <div className="space-y-0">
              {Object.entries(groupEventsByDay(events)).map(([dateKey, dayEvents]) => (
                <div key={dateKey} className="relative">
                  {dayEvents.map((event, index) => {
                    const isExpanded = isEventExpanded(event.id);
                    const hasChanges = event.changes && Object.keys(event.changes).length > 0;
                    const isLast = index === dayEvents.length - 1;
                    const eventIndex = events.findIndex(e => e.id === event.id);

                    return (
                      <div key={event.id} className="relative">
                        {/* Rail node (the dot) */}
                        <span
                          ref={(el) => {
                            if (markerRefs.current[eventIndex]) {
                              markerRefs.current[eventIndex].current = el;
                              
                            }
                          }}
                          className={`absolute left-[22px] top-6 h-2.5 w-2.5 rounded-full ${
                            (MODULE_COLORS[event.module?.toUpperCase()] ?? MODULE_COLORS.LEADS).bg
                          } border ${(MODULE_COLORS[event.module?.toUpperCase()] ?? MODULE_COLORS.LEADS).border}`}
                          aria-hidden
                        />
                        
                        {/* Module Node - White Circle with Color-Coded Border */}
                        <div className={`absolute left-2 top-6 w-8 h-8 rounded-full border-2 shadow-sm z-20 ${getModuleBorderColor(event.module)}`}>
                          <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                            <div className={`${getModuleTextColor(event.module)}`}>
                      {getModuleIcon(event.module)}
                    </div>
                  </div>
                        </div>
                        
                        {/* Connecting Line from Module to Card */}
                        <div className={`absolute left-6 top-9 ${getModuleLineColor(event.module)} ${density === 'condensed' ? 'w-6 h-0.5' : 'w-10 h-0.5'}`}></div>
                    
                        {/* Event Card */}
                        <div 
                          className={`bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group ${
                            density === 'condensed' ? 'ml-12 mr-4 mb-4' : 'ml-16 mr-8 mb-6'
                          }`}
                          onClick={() => toggleEventExpansion(event.id)}
                        >
                          {/* Header Row - New Hierarchy */}
                          <div className={`border-b border-gray-100 ${density === 'condensed' ? 'px-4 py-3' : 'px-6 py-4'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                {/* Module Name - Color Coded */}
                                <span className={`text-xs font-mono font-semibold tracking-wide ${getModuleTextColor(event.module)}`}>
                                  {event.module.toUpperCase()}
                                </span>
                                {/* Action Chip */}
                                <span className={`inline-flex items-center ${density === 'condensed' ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-xs'} rounded-full font-medium border ${getActionColor(event.action)}`}>
                        {event.action.toUpperCase()}
                      </span>
                              </div>
                              
                              {/* User, Time, and Date Info */}
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-1 text-xs text-gray-500">
                                  <div className="w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                                    <UserIcon className="h-2.5 w-2.5" />
                                  </div>
                                  <span className="font-medium">{event.userName || 'Unknown User'}</span>
                                </div>
                                <span className="text-xs text-gray-500" title={formatDate(event.createdAt)}>
                                  {formatRelativeTime(event.createdAt)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatDate(event.createdAt)}
                                </span>
                                
                                {/* Navigation Button */}
                                <button
                                  onClick={(e) => handleNavigateToEntity(event, e)}
                                  disabled={loadingEntity}
                                  className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={`View ${event.module} details`}
                                >
                                  {loadingEntity ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                                  ) : (
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  )}
                                </button>
                                
                                {/* Delete Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEventToDelete(event.id);
                                    setShowDeleteConfirm(true);
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                    </div>

                          {/* Primary Line (Summary) - Hero Element */}
                          <div className={`${density === 'condensed' ? 'px-4 py-3' : 'px-6 py-4'}`}>
                            <div className={`text-gray-900 font-semibold leading-relaxed ${density === 'condensed' ? 'text-sm' : 'text-base'}`}>
                      {event.description}
                            </div>
                    </div>

                          {/* Details Panel (Collapsed by default) */}
                          {(hasChanges || (event.metadata && Object.keys(event.metadata).length > 0)) && isExpanded && (
                            <div className={`pb-4 border-t border-gray-100 ${density === 'condensed' ? 'px-4' : 'px-6'}`}>
                              {/* Field-level diffs for updates */}
                    {hasChanges && (
                                <div className="mt-4">
                                  <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">Changes</h4>
                                  <div className="space-y-2">
                              {Object.entries(event.changes || {}).map(([field, change]) => (
                                      <div key={field}>
                                        {formatFieldDiff(field, change)}
                                </div>
                              ))}
                            </div>
                      </div>
                    )}

                              {/* Metadata details */}
                    {event.metadata && Object.keys(event.metadata).length > 0 && (
                                <div className="mt-4">
                                  <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">Details</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(event.metadata).map(([key, value]) => (
                                <div key={key} className="text-sm">
                                        <span className="font-medium text-gray-600 capitalize">
                                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                                        </span>
                                        <span className="text-gray-900 ml-2">{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    </div>
                    
                        {/* Last Event Line End */}
                        {isLast && (
                          <div className={`absolute left-6 top-9 w-px h-4 ${getModuleLineColor(event.module)}`}></div>
                        )}
              </div>
            );
          })}
                </div>
              ))}
            </div>
        </div>
      )}
      </div>

      {/* Load More Button */}
      {events.length > 0 && hasMore && (
        <div className="bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <span>Load More Events</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
          title="Back to top"
        >
          <ArrowUpIcon className="h-6 w-6" />
        </button>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-50 rounded-full">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Timeline Event</h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                  Are you sure you want to delete this timeline event? This action cannot be undone.
                </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setEventToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => eventToDelete && handleDeleteEvent(eventToDelete)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Detail Modal */}
      {showCallModal && selectedCall && (
        <CallDetailModal
          call={selectedCall}
          onClose={() => {
            setShowCallModal(false);
            setSelectedCall(null);
          }}
          onReschedule={(callId, newDate, reason) => {
            // Handle reschedule - you can implement this if needed
           
          }}
          onCancel={(callId, reason) => {
            // Handle cancel - you can implement this if needed
            
          }}
        />
      )}

      {/* Email Detail Modal */}
      {showEmailModal && selectedEmail && (
        <EmailDetailModal
          email={selectedEmail}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedEmail(null);
          }}
        />
      )}


      {/* Form Detail Modal */}
      {showFormModal && selectedForm && (
        <FormDetailModal
          form={selectedForm}
          onClose={() => {
            setShowFormModal(false);
            setSelectedForm(null);
          }}
        />
      )}

      {/* Note Detail Modal - Simple implementation */}
      {showNoteModal && selectedNote && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Note Details</h3>
                <button
                  onClick={() => {
                    setShowNoteModal(false);
                    setSelectedNote(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Note Content */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Content</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {selectedNote.content}
                  </p>
                </div>

                {/* Lead Information */}
                {selectedNote.leadName && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Lead</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedNote.leadName}
                      {selectedNote.leadEmail && ` (${selectedNote.leadEmail})`}
                    </p>
                  </div>
                )}

                {/* Creator Information */}
                {selectedNote.creatorName && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Created By</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedNote.creatorName}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedNote.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedNote.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for entity fetching */}
      {loadingEntity && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">Loading details...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadTimelineTab;


